/* eslint-disable indent */
/* eslint-disable react/no-array-index-key */
import LoadingIcon from '@/components/LoadingIcon';
import {
  useLatestTreatmentRecommendation,
  useNCCNGuideline,
  useTreatmentRecommendation,
} from '@/pages/combinedWard/hook/useTreatmentPlan';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MenuUnfoldOutlined,
  ReadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Button, Select, message } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import MarkdownIt from 'markdown-it';
import React, { useEffect, useRef, useState } from 'react';
import MdEditor, { Plugins } from 'react-markdown-editor-lite';
import styles from './index.less';

MdEditor.unuse(Plugins.ListOrdered);
MdEditor.unuse(Plugins.ListUnordered);

/**
 * 治疗方案推荐组件属性接口
 * @interface TreatmentRecommendationProps
 */
interface TreatmentRecommendationProps {
  /**
   * 患者ID
   */
  patientId?: string;
  /**
   * 计划类型
   */
  planType?: 'treatment' | 'qa' | null;
  /**
   * 样式
   */
  style?: React.CSSProperties | undefined;
  /**
   * 关闭回调
   */
  onClose?: () => void;
}

/**
 * 基于NCCN指南的治疗方案推荐组件
 * 支持分析病例、确认NCCN指南、生成治疗方案建议功能
 *
 * @component TreatmentRecommendation
 * @param {TreatmentRecommendationProps} props - 组件属性
 * @returns {React.ReactElement} 渲染的组件
 *
 * @example
 * // 基本用法
 * <TreatmentRecommendation />
 */
const TreatmentRecommendation: React.FC<TreatmentRecommendationProps> = ({
  patientId,
  style,
  planType,
  onClose,
}) => {
  // 思考内容的折叠状态
  const [isThinkingCollapsed, setIsThinkingCollapsed] = useState(true);
  // 思考中的动态省略号
  const [thinkingDots, setThinkingDots] = useState('');

  const mdParser = new MarkdownIt({});
  const {
    guidelineData,
    nccnConfirmVisible,
    analyzing,
    selectedNccnType,
    setSelectedNccnType,
    setNccnConfirmVisible,
    setGuidelineParams,
  } = useNCCNGuideline();
  const {
    setTreatmentParams,
    connectLoading,
    generatedTime,
    thinkingContent,
    answerContent,
    isAnswering,
    setThinkingContent,
    setAnswerContent,
    thinking,
    sessionId,
    cursor,
    isReconnecting,
    isCompleted,
    reconnect,
    restoreAndReconnect,
    clearSession,
    guidelineNameRef, // 添加 guidelineNameRef
  } = useTreatmentRecommendation();
  // 获取最新治疗推荐数据
  const { latestTreatmentData, latestLoading, setLatestTreatmentData, setPatientId } =
    useLatestTreatmentRecommendation();

  // 滚动相关状态
  const treatmentContentRef = useRef<HTMLDivElement>(null);
  const userScrollTimeout = useRef<NodeJS.Timeout>();
  const isInitialLoad = useRef(true);
  const lastScrollPosition = useRef(0);
  const lastScrollHeight = useRef(0);
  const autoScrollEnabledRef = useRef(true);

  // 标记是否已恢复会话，避免重复处理
  const hasRestoredSession = useRef(false);
  // 记录上次处理的 patientId，避免重复判断
  const lastProcessedPatientId = useRef<string>('');

  // 核心滚动逻辑
  const smartScroll = () => {
    if (!treatmentContentRef.current) return;

    const container = treatmentContentRef.current;
    const { scrollHeight, clientHeight, scrollTop } = container;

    // 动态阈值：可视区域高度的20%
    const dynamicThreshold = clientHeight * 0.2;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - dynamicThreshold;
    const contentHeightChange = scrollHeight - lastScrollHeight.current;
    const isHeightChangedSignificantly = contentHeightChange > clientHeight * 0.3;

    // 满足以下条件自动滚动：
    // 1. 自动滚动功能启用
    // 2. 正在生成回答或连接中
    // 3. 用户接近底部或内容高度变化显著
    if (
      autoScrollEnabledRef.current &&
      (isAnswering || connectLoading) &&
      (isNearBottom || isHeightChangedSignificantly || isInitialLoad.current)
    ) {
      container.scrollTo({
        top: scrollHeight,
        behavior: 'auto',
      });
      // 初始加载完成后重置标记
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
    }
    lastScrollHeight.current = scrollHeight;
  };

  // 内容更新时触发滚动
  useEffect(() => {
    // 使用requestAnimationFrame确保DOM更新后再滚动
    requestAnimationFrame(() => {
      smartScroll();
    });
  }, [thinkingContent, answerContent, isAnswering, connectLoading]);

  // 处理用户滚动交互
  useEffect(() => {
    const container = treatmentContentRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollHeight, clientHeight, scrollTop } = container;
      const currentPosition = scrollTop + clientHeight;

      // 计算当前滚动位置与底部的距离
      const distanceToBottom = scrollHeight - currentPosition;

      // 如果用户主动滚动离开底部区域（距离大于20px），禁用自动滚动
      if (distanceToBottom > 20 && autoScrollEnabledRef.current) {
        autoScrollEnabledRef.current = false;

        // 清除已有定时器
        if (userScrollTimeout.current) {
          clearTimeout(userScrollTimeout.current);
        }
      } else if (distanceToBottom <= 20 && !autoScrollEnabledRef.current) {
        // 如果用户滚动回到底部区域，重新启用自动滚动
        autoScrollEnabledRef.current = true;
      }

      // 5秒后恢复自动滚动（仅在生成回答或连接中时）
      if (!autoScrollEnabledRef.current && (isAnswering || connectLoading)) {
        if (userScrollTimeout.current) {
          clearTimeout(userScrollTimeout.current);
        }

        userScrollTimeout.current = setTimeout(() => {
          autoScrollEnabledRef.current = true;
          smartScroll();
        }, 5000);
      }

      lastScrollPosition.current = currentPosition;
    };

    // 启用滚动事件监听器
    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      // 移除滚动事件监听器
      container.removeEventListener('scroll', handleScroll);
      if (userScrollTimeout.current) {
        clearTimeout(userScrollTimeout.current);
      }
    };
  }, [isAnswering, connectLoading]);

  // 回答状态变化时重置自动滚动
  useEffect(() => {
    if (isAnswering) {
      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        isInitialLoad.current = true;
        smartScroll();
      });
    }
  }, [isAnswering]);

  // 初始加载和数据更新时滚动到底部
  useEffect(() => {
    if ((thinkingContent || answerContent) && !isAnswering && isInitialLoad.current) {
      requestAnimationFrame(() => {
        smartScroll();
      });
    }
  }, [thinkingContent, answerContent, isAnswering]);

  useEffect(() => {
    // 只在 treatment 类型且 patientId 有效时处理
    if (!patientId || planType !== 'treatment') {
      return;
    }

    // 如果是同一个 patientId，避免重复处理
    if (lastProcessedPatientId.current === patientId) {
      return;
    }

    // 更新已处理的 patientId
    lastProcessedPatientId.current = patientId;

    // 优先检查是否有未完成的会话需要恢复
    const hasRestored = restoreAndReconnect(patientId);

    if (hasRestored) {
      // 标记已恢复会话
      hasRestoredSession.current = true;
      message.info('检测到未完成的生成任务，正在续传...');
    } else {
      // 没有未完成会话，获取最新治疗推荐数据
      hasRestoredSession.current = false;
      setPatientId(patientId);
    }
  }, [patientId, planType]);

  useEffect(() => {
    if (latestTreatmentData) {
      setIsThinkingCollapsed(true);
      let aiAnswer = '';
      let aiThinking = '';
      if (latestTreatmentData?.aiContent?.indexOf('</think>') !== -1) {
        aiAnswer = latestTreatmentData?.aiContent?.split('</think>')[1];
        aiThinking = latestTreatmentData?.aiContent
          ?.split('</think>')[0]
          ?.replace('<think>', '')
          ?.replace('[br]', '');
      } else {
        aiAnswer = latestTreatmentData?.aiContent;
        aiThinking = '';
      }

      setThinkingContent(aiThinking);
      setAnswerContent(aiAnswer);

      // 清除可能存在的未完成会话（因为已经有完整的历史数据了）
      clearSession(patientId);
      hasRestoredSession.current = false;
    }
  }, [latestTreatmentData]);

  // 处理思考中的动态省略号效果
  useEffect(() => {
    let dotsTimer: NodeJS.Timeout;
    if (thinking) {
      dotsTimer = setInterval(() => {
        setThinkingDots((prev) => {
          if (prev.length >= 3) return '';
          return `${prev}.`;
        });
      }, 500);
    }
    return () => {
      clearInterval(dotsTimer);
      setThinkingDots('');
    };
  }, [thinking]);

  // 网络状态监听，自动重连
  useEffect(() => {
    const handleOnline = () => {
      if (sessionId && !isCompleted && !isAnswering) {
        message.info('网络已恢复，正在续传...');
        reconnect();
      }
    };

    const handleOffline = () => {
      if (isAnswering) {
        message.warning('网络已断开，数据已保存，恢复后将自动续传');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [sessionId, isCompleted, isAnswering]);

  /**
   * 分析病例数据
   * 模拟API调用加载NCCN指南选择确认
   */
  const analyzePatientData = () => {
    if (patientId) {
      // API调用获取NCCN指南列表
      setGuidelineParams(patientId);
      setSelectedNccnType(undefined); // 初始化为未选中状态
    }
  };

  /**
   * 处理NCCN指南选择变更
   */
  const handleNccnChange = (value: number | undefined, option: any) => {
    setSelectedNccnType(value);
  };

  /**
   * 确认NCCN指南并进入下一步
   */
  const confirmNccnGuide = () => {
    if (selectedNccnType === undefined) {
      message.error('请选择NCCN指南');
      return;
    }
    setTreatmentParams({
      patientId,
      guidelineName: selectedNccnType,
    });
    setNccnConfirmVisible(false);
    setIsThinkingCollapsed(false);
  };

  /**
   * 重新生成治疗方案
   */
  const regenerateTreatmentPlan = () => {
    // 清除旧的会话状态
    clearSession(patientId);
    // 重置标记
    hasRestoredSession.current = false;
    lastProcessedPatientId.current = '';
    setLatestTreatmentData(null);
    setNccnConfirmVisible(false);
    setIsThinkingCollapsed(false);

    // 优先使用 guidelineNameRef.current（最新保存的值）
    // 其次使用 selectedNccnType（当前选中的值）
    // 最后使用 latestTreatmentData?.guideName（历史数据）
    const guidelineName =
      guidelineNameRef.current || selectedNccnType || latestTreatmentData?.guideName;

    if (!guidelineName) {
      message.error('无法获取指南名称，请重新选择');
      return;
    }

    setTreatmentParams({
      patientId,
      guidelineName,
    });
  };

  return (
    <div style={style} className={styles.treatmentContentWrapper}>
      <div className={styles.treatmentHeader}>
        <div className={styles.treatmentTitle}>治疗方案推荐</div>
        <div className={styles.treatmentActions}>
          {(thinkingContent || answerContent) && (
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined />}
              shape="round"
              onClick={regenerateTreatmentPlan}
            >
              重新生成
            </Button>
          )}
          <MenuUnfoldOutlined
            style={{ fontSize: '16px', cursor: 'pointer', color: '#0b214573' }}
            onClick={onClose}
          />
        </div>
      </div>

      {(analyzing || latestLoading) && (
        <div className={styles.loadingContainer}>
          <LoadingIcon
            animation={false}
            description={latestLoading ? '获取数据中...' : '正在分析病例数据...'}
          />
        </div>
      )}

      {connectLoading && !isReconnecting && !thinkingContent && !answerContent && (
        <div className={styles.loadingContainer}>
          <LoadingIcon animation={false} description="正在生成治疗方案推荐..." />
          <p className={styles.loadingSubText}>分析病例数据并结合NCCN指南</p>
        </div>
      )}

      {(thinkingContent || answerContent) && (
        <div className={styles.treatmentPlanResult}>
          <div className={styles.treatmentContent} ref={treatmentContentRef}>
            <div className={styles.generatedTimeInfo}>
              <div className={styles.generatedTime}>
                生成时间: {latestTreatmentData?.createTime || generatedTime}
              </div>
            </div>
            <div className={styles.nccnGuideInfo}>
              <h3>{latestTreatmentData?.guideName || selectedNccnType}</h3>
              <p className={styles.disclaimer}>
                以下治疗方案根据患者的具体情况自动生成，请结合临床实际情况进行判断
              </p>
            </div>
            {thinkingContent && (
              <div
                className={classNames(styles.thinkingContentTitle, {
                  [styles.thinkingActive]: thinking,
                })}
                onClick={() => setIsThinkingCollapsed(!isThinkingCollapsed)}
              >
                <span className={styles.collapseIcon}>
                  {isThinkingCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                </span>
                {thinking ? (
                  <span className={styles.thinkingStatus}>
                    <LoadingOutlined style={{ marginLeft: 4, fontSize: 14 }} spin />{' '}
                    <span>思考中{thinkingDots}</span>
                  </span>
                ) : (
                  '已深度思考'
                )}
                {/* <span className={styles.timeInfo}>(用时 24 秒)</span> */}
              </div>
            )}

            <div
              className={classNames(styles.thinkingContentWrapper, {
                [styles.collapsed]: isThinkingCollapsed,
                [styles.expanded]: !isThinkingCollapsed,
              })}
            >
              <MdEditor
                value={thinkingContent?.replace('[br]', '')}
                readOnly
                htmlClass={classNames(
                  'custom-html-style',
                  styles['editor-html-style'],
                  styles['thinking-content'],
                )}
                markdownClass={classNames(styles['editor-md-style'])}
                view={{
                  menu: false,
                  md: false,
                  html: true,
                }}
                renderHTML={(text) => mdParser.render(text)}
              />
            </div>
            <MdEditor
              value={answerContent}
              readOnly
              htmlClass={classNames('custom-html-style', styles['editor-html-style'])}
              markdownClass={classNames(styles['editor-md-style'])}
              view={{
                menu: false,
                md: false,
                html: true,
              }}
              renderHTML={(text) => mdParser.render(text)}
            />

            {isReconnecting && (
              <div className={styles.reconnectinContainer}>
                <LoadingIcon animation={false} description="正在续传, 请稍候..." />
                {/* <p className={styles.loadingSubText}>已生成 {cursor} 字符</p> */}
              </div>
            )}
            <div className={styles.qaAnswerMeta}>
              {(thinkingContent || answerContent) && !isAnswering && (
                <span className={styles.qaAnswerInfo}>
                  <ExclamationCircleOutlined className={styles.infoIcon} />
                  本回答由AI生成，内容仅供参考
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {nccnConfirmVisible && (
        <div className={styles.nccnConfirmContainer}>
          <div className={styles.nccnIconContainer}>
            <ReadOutlined style={{ fontSize: 60 }} />
          </div>
          <div className={styles.nccnConfirmTitle}>
            基于上传的病例信息，系统已匹配相关的NCCN指南，请您确认后，进行下一步
          </div>
          <div className={styles.nccnSelectContainer}>
            <Select
              style={{ width: '300px', textAlign: 'left' }}
              value={selectedNccnType}
              onChange={handleNccnChange}
              options={guidelineData?.map((item: any) => ({
                value: item,
                label: item,
              }))}
              placeholder="请选择NCCN指南"
              allowClear
            />
          </div>
          <div className={styles.nccnConfirmActions}>
            <Button
              disabled={!selectedNccnType}
              shape="round"
              type="primary"
              onClick={confirmNccnGuide}
            >
              下一步
            </Button>
          </div>
        </div>
      )}

      {!analyzing &&
        !connectLoading &&
        !nccnConfirmVisible &&
        !latestTreatmentData &&
        !latestLoading &&
        !thinkingContent &&
        !answerContent && (
          <div className={styles.initialStateContainer}>
            <div className={styles.iconContainer}>
              <ReadOutlined style={{ fontSize: 60 }} />
            </div>
            <h3 className={styles.initialTitle}>基于NCCN指南生成治疗推荐</h3>
            <p className={styles.initialDescription}>
              系统将分析患者的病例信息，结合NCCN指南，生成个性化的治疗方案建议
            </p>
            <Button
              className={styles.generateButton}
              type="primary"
              icon={
                <span
                  style={{ marginRight: 4 }}
                  className="iconfont icon-shengchengzhiliaofangan"
                />
              }
              shape="round"
              onClick={analyzePatientData}
            >
              生成治疗方案
            </Button>
          </div>
        )}
    </div>
  );
};

export default TreatmentRecommendation;
